import {
  Toast,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  type ToastTone,
  ToastViewport,
} from '@hurdl/design-system';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';

/**
 * The panel's toast feedback: a {@link ToastHost} the Layout mounts once, and a {@link
 * useToast} the pages raise reports through.
 *
 * A submission's outcome — "Reviewed Big Mac", a save, a failure — is reported in a
 * toast rather than inline, so the Operator keeps working while the report fades on its
 * own. The platform has no global store, so this is the smallest robust shape: a React
 * context holding an array of pending reports and a `notify` that appends one. The host
 * renders the Design_System's `ToastProvider` and `ToastViewport` and maps each report
 * to a `Toast`; a report removes itself from the array when the primitive closes it,
 * which it does after the provider's default duration.
 *
 * The duration is the primitive's own — a real runtime, not a Fixture, so a timer is
 * right and the report auto-dismisses. A Fixture that wanted a toast still on screen
 * would render one with `duration={Infinity}`; the host here lets the default stand.
 */

/** One pending report: its identity, tone, title and optional supporting line. */
interface ToastReport {
  readonly id: number;
  readonly tone: ToastTone;
  readonly title: string;
  readonly description?: string;
}

/** What {@link useToast} answers: the one function a page raises a report through. */
interface ToastController {
  /** Raise a report. It appears, is announced, and fades on its own. */
  notify: (tone: ToastTone, title: string, description?: string) => void;
}

const ToastContext = createContext<ToastController | null>(null);

/**
 * Holds the pending reports and renders them, around the subtree the pages live in.
 *
 * Mounted once in the Layout, inside the application's `ThemeSurface`, so the viewport
 * resolves the theme by inheritance (the Design_System's `ToastViewport` renders in
 * place rather than through a portal, so it needs no theme re-application). A page calls
 * `useToast().notify(...)`; the host appends a report, and the `Toast` the host renders
 * for it removes it from the array when the primitive closes it — on dismissal, or after
 * the auto-dismiss timer.
 */
export function ToastHost({
  children,
}: {
  readonly children: ReactNode;
}): ReactElement {
  const [reports, setReports] = useState<ReadonlyArray<ToastReport>>([]);

  const notify = useCallback(
    (tone: ToastTone, title: string, description?: string): void => {
      setReports((prior) => [
        ...prior,
        {
          id: Date.now() + prior.length,
          tone,
          title,
          ...(description !== undefined ? { description } : {}),
        },
      ]);
    },
    [],
  );

  const remove = useCallback((id: number): void => {
    setReports((prior) => prior.filter((report) => report.id !== id));
  }, []);

  const controller = useMemo<ToastController>(() => ({ notify }), [notify]);

  return (
    <ToastContext.Provider value={controller}>
      <ToastProvider>
        {children}
        {reports.map((report) => (
          <Toast
            key={report.id}
            tone={report.tone}
            open
            onOpenChange={(open) => {
              if (!open) remove(report.id);
            }}
          >
            <div className="min-w-0 grow">
              <ToastTitle>{report.title}</ToastTitle>
              {report.description !== undefined ? (
                <ToastDescription>{report.description}</ToastDescription>
              ) : undefined}
            </div>
            <ToastClose />
          </Toast>
        ))}
        <ToastViewport />
      </ToastProvider>
    </ToastContext.Provider>
  );
}

/**
 * The one function a page raises a report through, or a thrown error naming a caller
 * outside a {@link ToastHost}.
 */
export function useToast(): ToastController {
  const controller = useContext(ToastContext);
  if (controller === null) {
    throw new Error('useToast must be called within a ToastHost.');
  }
  return controller;
}
